import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { signIn, signUp, confirmSignUp } from "aws-amplify/auth";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/home";
  const [mode, setMode] = useState<"login" | "signup" | "confirm">("login");

  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("client");
  const [code, setCode] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const result = await signIn({ username: email, password });
      console.log("Login result:", result);
      // Wait briefly for tokens to be stored
      setTimeout(() => {
        const token = Object.keys(localStorage).find(k => k.includes("idToken"));
        if (token) {
          const t = localStorage.getItem(token);
          if (t && t.split(".").length === 3) {
            try {
              const p = JSON.parse(atob(t.split(".")[1]));
              const r = p["custom:role"] || "client";
              localStorage.setItem("mouna_user_role", r);
              console.log("Final role:", r);
            } catch {}
          }
        }
        window.location.href = redirectTo;
      }, 500);
    } catch (err: any) {
      if (err.message?.includes("already a signed in user")) {
        window.location.href = redirectTo;
        return;
      }
      setError(err.message || "Sign in failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    if (!agreedToTerms) {
      setError("Please agree to the Terms of Service and Privacy Policy to continue");
      return;
    }
    setLoading(true);
    setError("");
    try {
     await signUp({
        username: email,
        password,
        options: { userAttributes: { email, given_name: firstName.trim(), "custom:role": "client" } }
      });
      setMode("confirm");
    } catch (err: any) {
      setError(err.message || "Sign up failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      setMode("login");
      setError("");
    } catch (err: any) {
      setError(err.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <svg width="44" height="44" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="loginLg1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#4C6EF5"/><stop offset="100%" stopColor="#1A3CE8"/></linearGradient>
              <linearGradient id="loginLg2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#20C997"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
              <linearGradient id="loginBg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#3B5BDB"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
            </defs>
            <rect width="52" height="52" rx="14" fill="url(#loginBg)"/>
            <g transform="translate(4,5)">
              <path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.95"/>
              <circle cx="11" cy="10" r="5" fill="url(#loginLg1)"/>
              <circle cx="11" cy="10" r="2.5" fill="white"/>
            </g>
            <g transform="translate(25,5)">
              <path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.85"/>
              <circle cx="11" cy="10" r="5" fill="url(#loginLg2)"/>
              <circle cx="11" cy="10" r="2.5" fill="white"/>
            </g>
          </svg>
          <span className="font-display text-2xl font-bold text-navy">Mouna</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-md animate-slide-up">

          {/* Hero text */}
          <div className="text-center mb-8">
            <h1 className="font-display text-3xl font-bold text-navy mb-2">
              {mode === "login" ? "Welcome back" :
               mode === "signup" ? "Create account" :
               "Verify your email"}
            </h1>
            <p className="text-gray-500 text-sm">
              {mode === "login" ? "Sign in to your Mouna account" :
               mode === "signup" ? "Join thousands of happy clients" :
               `We sent a code to ${email}`}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-2xl px-4 py-3 mb-5 animate-fade-in">
              {error}
            </div>
          )}

          {/* Login form */}
          {mode === "login" && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="form-section space-y-4">
                <div className="form-group mb-0">
                  <label className="label">Email Address</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com" required
                    className="input"/>
                </div>
                <div className="form-group mb-0">
                  <div className="flex items-center justify-between mb-1">
                    <label className="label mb-0">Password</label>
                    <button type="button" onClick={() => navigate("/forgot-password")}
                      className="text-xs text-mouna-blue font-semibold hover:underline">
                      Forgot?
                    </button>
                  </div>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Your password" required
                    className="input"/>
                </div>
              </div>

              <button type="submit" disabled={loading}
                className="btn btn-primary btn-full btn-lg">
                {loading ? "Signing in..." : "Sign In"}
              </button>

              <div className="text-center text-sm text-gray-500">
                No account?{" "}
                <button type="button" onClick={() => { setMode("signup"); setError(""); }}
                  className="text-mouna-blue font-semibold hover:underline">
                  Sign up
                </button>
              </div>

              <div className="flex items-center gap-3 my-2">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-xs text-gray-400">or</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>

              <button type="button" onClick={() => navigate("/pro-signup")}
                className="btn btn-secondary btn-full">
                🔨 Join as a Professional
              </button>
            </form>
          )}

          {/* Sign up form */}
          {mode === "signup" && (
            <form onSubmit={handleSignUp} className="space-y-4">
              <div className="form-section">
                <div className="bg-blue-pale rounded-xl p-3 mb-4">
                  <p className="text-xs text-gray-500">
                    Creating a <span className="font-bold text-navy">client</span> account.{" "}
                    Want to work as a pro?{" "}
                    <button type="button" onClick={() => navigate("/pro-signup")}
                      className="text-mouna-blue font-bold">
                      Join as a Professional →
                    </button>
                  </p>
                </div>
              <div className="form-group">
                  <label className="label">First Name</label>
                  <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)}
                    placeholder="Your first name" required className="input"/>
                </div>
                <div className="form-group">
                  <label className="label">Email Address</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com" required className="input"/>
                </div>
                <div className="form-group mb-0">
                  <label className="label">Password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Min 8 characters" required className="input"/>
                </div>
              </div>

              <label className="flex items-start gap-3 px-1 cursor-pointer">
                <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-mouna-blue flex-shrink-0"/>
                <span className="text-xs text-gray-500 leading-relaxed">
                  I agree to the{" "}
                  <Link to="/terms" className="text-mouna-blue underline">Terms of Service</Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="text-mouna-blue underline">Privacy Policy</Link>
                </span>
              </label>

             <button type="submit" disabled={loading || !agreedToTerms}
                className="btn btn-primary btn-full btn-lg">
                {loading ? "Creating account..." : "Create Account"}
              </button>

              <div className="text-center text-sm text-gray-500">
                Already have an account?{" "}
                <button type="button" onClick={() => { setMode("login"); setError(""); }}
                  className="text-mouna-blue font-semibold hover:underline">
                  Sign in
                </button>
              </div>
            </form>
          )}

          {/* Confirm form */}
          {mode === "confirm" && (
            <form onSubmit={handleConfirm} className="space-y-4">
              <div className="form-section">
                <div className="form-group mb-0">
                  <label className="label">Verification Code</label>
                  <input type="text" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000" required
                    className="input text-center text-2xl tracking-widest font-bold"/>
                </div>
              </div>

              <button type="submit" disabled={loading}
                className="btn btn-primary btn-full btn-lg">
                {loading ? "Verifying..." : "Verify & Continue"}
              </button>

              <div className="text-center text-sm text-gray-500">
                Wrong email?{" "}
                <button type="button" onClick={() => setMode("signup")}
                  className="text-mouna-blue font-semibold hover:underline">
                  Go back
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
